import { useCallback, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

/**
 * Errors a form can point at.
 *
 * The server answers a refusal with `detail` (one sentence) and, when it knows which
 * box is to blame, `fields` — `{ column: message }`. Before this, every refusal went
 * to a toast at the bottom-right of the screen while the empty box that caused it sat
 * unmarked; "Title and Unit are required" is not an instruction anybody can act on
 * without re-reading the whole form.
 *
 * One hook for every form. A field error is shown under its box and the first one is
 * focused; only what no box can carry goes to a toast. Client-side checks feed the
 * same map, so a message looks the same whether the browser or the server said it.
 */

/** { fields, message } from an axios error, or from the response body itself. */
export function errorsFromResponse(err, fallback = "Something went wrong.") {
  const body = err?.response?.data ?? err ?? {};
  const fields = body && typeof body.fields === "object" && body.fields ? body.fields : {};
  return { fields, message: formatApiError(body?.detail, fallback) };
}

/** The id an error message is rendered under, so the box can point at it. */
export const errorId = (field) => `err-${field}`;

export function useFieldErrors() {
  const [errors, setErrors] = useState({});

  const clear = useCallback((field) => {
    setErrors((e) => {
      if (!field) return {};
      if (!(field in e)) return e;
      const next = { ...e };
      delete next[field];
      return next;
    });
  }, []);

  // Focus the first box that carries an error, in the order the DOM renders them —
  // which is the order the person reads them.
  const focusFirst = useCallback((fields) => {
    const names = Object.keys(fields);
    if (!names.length) return;
    requestAnimationFrame(() => {
      const el = document.querySelector(names.map((n) => `[data-field="${n}"]`).join(","));
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      const target = el.matches("input,textarea,select,button") ? el : el.querySelector("input,textarea,select,button");
      target?.focus?.({ preventScroll: true });
    });
  }, []);

  /**
   * Show a refusal. Field messages go under their boxes; the sentence goes to a toast
   * only when no box on this form could carry it. `known` is the set of fields the
   * form renders — an error about a field it does not show must not vanish.
   */
  const report = useCallback((errOrShape, { fallback, known } = {}) => {
    const { fields, message } = errOrShape?.fields && !errOrShape.response
      ? { fields: errOrShape.fields, message: errOrShape.message || Object.values(errOrShape.fields).join(" ") }
      : errorsFromResponse(errOrShape, fallback);
    const shown = known ? Object.fromEntries(Object.entries(fields).filter(([k]) => known.includes(k))) : fields;
    setErrors(shown);
    if (Object.keys(shown).length) focusFirst(shown);
    if (!Object.keys(shown).length || Object.keys(fields).length > Object.keys(shown).length) {
      toast.error(message);
    }
    return Object.keys(shown).length > 0;
  }, [focusFirst]);

  /** Props for the box itself: marks it invalid and ties it to its message. */
  const fieldProps = useCallback((field) => ({
    "data-field": field,
    "aria-invalid": errors[field] ? true : undefined,
    "aria-describedby": errors[field] ? errorId(field) : undefined,
  }), [errors]);

  return { errors, report, clear, fieldProps };
}
